import firebase from 'firebase/compat/app';
import { db } from './config';
import {
  BoulderCompetitionData,
  BoulderAthlete,
  BoulderClass,
  BoulderSportClass,
  Boulder,
  BoulderResult,
  DEFAULT_BOULDER_CLASSES,
  getDefaultData,
  resultKey,
} from '../utils/boulderCompetition';

/** New subcollection path: competitions/{competitionId}/boulder/config */
function docRef(competitionId: string): firebase.firestore.DocumentReference {
  return db.collection('competitions').doc(competitionId).collection('boulder').doc('config');
}

/** Legacy path used for auto-migration of bk2026 */
const LEGACY_DOC = () => db.collection('boulder-competition').doc('bk2026');

// ── Migration: old per-gender class IDs → merged group IDs ───────────────────
const OLD_TO_GROUP: Record<string, string> = {
  'c-al1-m': 'al1',
  'c-al2-m': 'al2',  'c-al2-f': 'al2',
  'c-au3-m': 'au3',  'c-au3-f': 'au3',
  'c-b1-f':  'b',    'c-b2-m':  'b',   'c-b3-m': 'b', 'c-b3-f': 'b',
  'c-rp1-m': 'rp1-au1', 'c-rp1-f': 'rp1-au1',
  'c-rp3-m': 'm-rp3', 'c-rp3-f': 'w-rp3',
};
const OLD_TO_PARACLIMBING: Record<string, string> = {
  'c-al1-m': 'AL1',
  'c-al2-m': 'AL2', 'c-al2-f': 'AL2',
  'c-au3-m': 'AU3', 'c-au3-f': 'AU3',
  'c-b1-f':  'B1',  'c-b2-m':  'B2',  'c-b3-m': 'B3', 'c-b3-f': 'B3',
  'c-rp1-m': 'RP1', 'c-rp1-f': 'RP1',
  'c-rp3-m': 'RP3', 'c-rp3-f': 'RP3',
};

/** Safely reconstruct data, filling in any missing fields with defaults.
 *  Also migrates old per-gender class IDs to the new merged group IDs. */
function mergeWithDefaults(raw: Record<string, unknown>): BoulderCompetitionData {
  const rawSetup = (raw.setup && typeof raw.setup === 'object' ? raw.setup : {}) as Record<string, unknown>;

  const boulders: Boulder[] = Array.isArray(rawSetup.boulders)
    ? (rawSetup.boulders as Boulder[])
    : [];

  // Migrate old per-gender classes to merged groups if needed
  const rawClasses: BoulderClass[] = Array.isArray(rawSetup.classes)
    ? (rawSetup.classes as BoulderClass[])
    : [];
  const needsMigration = rawClasses.some(c => c.id.startsWith('c-'));
  let classes: BoulderClass[];
  if (needsMigration) {
    const groupBids: Record<string, Set<string>> = {};
    DEFAULT_BOULDER_CLASSES.forEach(g => { groupBids[g.id] = new Set(); });
    rawClasses.forEach(c => {
      const gid = OLD_TO_GROUP[c.id];
      if (gid && groupBids[gid]) c.boulderIds.forEach(bid => groupBids[gid].add(bid));
    });
    classes = DEFAULT_BOULDER_CLASSES.map(g => ({
      ...g, boulderIds: Array.from(groupBids[g.id] ?? []),
    }));
  } else {
    // Use stored classes; for brand-new competitions the array is intentionally empty
    classes = rawClasses.length > 0 ? rawClasses : [];
  }

  const locked: boolean =
    typeof rawSetup.locked === 'boolean' ? rawSetup.locked : false;

  const sportClasses: BoulderSportClass[] = Array.isArray(rawSetup.sportClasses)
    ? (rawSetup.sportClasses as BoulderSportClass[])
    : [];

  // Migrate athlete classIds from old per-gender IDs to group IDs
  const rawAthletes: BoulderAthlete[] = Array.isArray(raw.athletes)
    ? (raw.athletes as BoulderAthlete[])
    : [];
  const athletes = rawAthletes.map(a => {
    const groupId = OLD_TO_GROUP[a.classId];
    if (!groupId) return a;
    return {
      ...a,
      classId: groupId,
      paraclimbingClass: (a as any).paraclimbingClass || OLD_TO_PARACLIMBING[a.classId],
    };
  });

  const results: Record<string, BoulderResult> =
    (raw.results && typeof raw.results === 'object' && !Array.isArray(raw.results))
      ? (raw.results as Record<string, BoulderResult>)
      : {};

  return { setup: { boulders, sportClasses, classes, locked }, athletes, results };
}

/** Empty data for brand-new competitions (no hardcoded BK2026 athletes). */
function emptyData(): BoulderCompetitionData {
  const defaults = getDefaultData();
  return { setup: { boulders: [], sportClasses: defaults.setup.sportClasses, classes: [], locked: false }, athletes: [], results: {} };
}

/**
 * Load once (admin mount).
 * Auto-migrates bk2026 from the legacy collection on first access.
 * Creates the document with empty data for new competitions.
 */
export async function loadBoulderData(competitionId: string): Promise<BoulderCompetitionData> {
  const ref = docRef(competitionId);
  let snap = await ref.get();

  if (!snap.exists && competitionId === 'bk2026') {
    // One-time auto-migration: copy from legacy collection path
    const legacySnap = await LEGACY_DOC().get();
    if (legacySnap.exists) {
      await ref.set(legacySnap.data()!);
      snap = await ref.get();
    }
  }

  if (snap.exists) {
    return mergeWithDefaults(snap.data() as Record<string, unknown>);
  }
  const empty = emptyData();
  await ref.set(empty);
  return empty;
}

/** Overwrite the whole document. */
export async function saveBoulderData(
  competitionId: string,
  data: BoulderCompetitionData,
): Promise<void> {
  await docRef(competitionId).set(data);
}

/**
 * Atomically update a single result entry.
 * Uses Firestore field-path update so concurrent jury members don't overwrite each other.
 */
export async function saveResultEntry(
  competitionId: string,
  athleteId: string,
  boulderId: string,
  result: BoulderResult,
): Promise<void> {
  const key = resultKey(athleteId, boulderId);
  await docRef(competitionId).update({ [`results.${key}`]: result });
}

/** Real-time listener. Returns an unsubscribe function. */
export function subscribeBoulderData(
  competitionId: string,
  onData: (data: BoulderCompetitionData) => void,
  onError?: (err: Error) => void,
): () => void {
  return docRef(competitionId).onSnapshot(
    snap => {
      if (snap.exists) {
        onData(mergeWithDefaults(snap.data() as Record<string, unknown>));
      } else {
        onData(emptyData());
      }
    },
    err => {
      console.error('Firestore boulder-competition listener error:', err);
      onError?.(err);
    },
  );
}
